import { c as _c } from "react/compiler-runtime";
import { Check, SquareArrowOutUpRight, MoreHorizontal } from "lucide-react";
import Link from "next/link";
import React from "react";
import { CardActions } from "@/src/components/cards/card-actions";
import { ComparisonField } from "@/src/components/cards/comparison-field";
import { CopyableContent } from "@/src/components/copyable-content";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { Button } from "@/src/components/shadcn/ui/button";
import { DropdownMenu, DropdownMenuTrigger } from "@/src/components/shadcn/ui/dropdown-menu";
import { cn } from "@/src/lib/shadcn-utils";
import { getNestedValue } from "@/src/lib/utils/object-utils";
import { compareStrings, getDisplayValue, normalizeForComparison, FieldType } from "@/src/lib/utils/string-utils";
export interface Field {
  key: string;
  label: string;
  type?: FieldType;
  comparable?: boolean;
  required?: boolean;
  colSpan?: 1 | 2 | 3;
  wrap?: boolean;
  hideIfEmpty?: boolean;
  sectionHeader?: string;
  getValue?: (obj: any) => string | null | undefined;
}
function resolveFieldValue(field: Field, obj: any): string | null | undefined {
  if (field.getValue) {
    return field.getValue(obj);
  }
  return getNestedValue(obj, field.key);
}
export interface FieldDisplayProps {
  field: Field;
  primaryValue: string | null;
  secondaryValue?: string | null;
  isCompareMode: boolean;
  compareAgainstTitle?: string;
}
export function FieldDisplay(t0) {
  const $ = _c(17);
  const {
    field,
    primaryValue,
    secondaryValue,
    isCompareMode,
    compareAgainstTitle: t1
  } = t0;
  const compareAgainstTitle = t1 === undefined ? "EMR" : t1;
  if (isCompareMode && secondaryValue !== null && field.comparable) {
    const t2 = primaryValue || "N/A";
    const t3 = secondaryValue || "N/A";
    const t4 = field.type ?? "text";
    let t5;
    if ($[0] !== compareAgainstTitle || $[1] !== field.label || $[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
      t5 = <ComparisonField label={field.label} value={t2} secondaryValue={t3} type={t4} compareAgainstTitle={compareAgainstTitle} />;
      $[0] = compareAgainstTitle;
      $[1] = field.label;
      $[2] = t2;
      $[3] = t3;
      $[4] = t4;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    return t5;
  }
  const t2 = field.type ?? "text";
  let t3;
  if ($[6] !== primaryValue || $[7] !== t2) {
    t3 = getDisplayValue(primaryValue, t2);
    $[6] = primaryValue;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const displayValue = t3;
  const t4 = field.wrap ? "break-words" : "overflow-hidden text-ellipsis whitespace-nowrap";
  let t5;
  if ($[9] !== field.label) {
    t5 = <span className="font-medium">{field.label}:</span>;
    $[9] = field.label;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== displayValue) {
    t6 = <CopyableContent text={displayValue} />;
    $[11] = displayValue;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <div className={t4}>{t5} {t6}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
export function FieldLayout(t0) {
  const $ = _c(15);
  const {
    fields,
    patient,
    secondaryObject,
    compareMode: t1,
    compareAgainstTitle: t2
  } = t0;
  const compareMode = t1 === undefined ? false : t1;
  const compareAgainstTitle = t2 === undefined ? "EMR" : t2;
  let t3;
  if ($[0] !== compareAgainstTitle || $[1] !== compareMode || $[2] !== fields || $[3] !== patient || $[4] !== secondaryObject) {
    let t4;
    if ($[6] !== patient) {
      t4 = field => !field.hideIfEmpty || resolveFieldValue(field, patient);
      $[6] = patient;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== compareAgainstTitle || $[9] !== compareMode || $[10] !== patient || $[11] !== secondaryObject) {
      t5 = field_0 => <React.Fragment key={field_0.key}>{field_0.sectionHeader && <div className="col-span-3 mt-1 border-t border-border pt-2"><span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{field_0.sectionHeader}</span></div>}<div className={cn(field_0.colSpan === 3 && "col-span-3", field_0.colSpan === 2 && "col-span-2", field_0.wrap && "min-w-0")}><FieldDisplay field={field_0} primaryValue={resolveFieldValue(field_0, patient) || "N/A"} secondaryValue={secondaryObject ? resolveFieldValue(field_0, secondaryObject) || "N/A" : null} isCompareMode={compareMode} compareAgainstTitle={compareAgainstTitle} /></div></React.Fragment>;
      $[8] = compareAgainstTitle;
      $[9] = compareMode;
      $[10] = patient;
      $[11] = secondaryObject;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t3 = fields.filter(t4).map(t5);
    $[0] = compareAgainstTitle;
    $[1] = compareMode;
    $[2] = fields;
    $[3] = patient;
    $[4] = secondaryObject;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[13] !== t3) {
    t4 = <div className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">{t3}</div>;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
export function hasConflicts(fields: Field[], obj: any, secondaryObj: any) {
  const comparableFields = fields.filter(field => field.comparable);
  return comparableFields.some(field => {
    const fieldType = field.type ?? "text";
    const primaryValue = normalizeForComparison(getNestedValue(obj, field.key) || "", fieldType);
    const secondaryValue = normalizeForComparison(getNestedValue(secondaryObj, field.key) || "", fieldType);
    return !compareStrings(primaryValue, secondaryValue, fieldType);
  });
}
export function renderBadge(compareMode: boolean, secondaryObject: any, hasAnyConflicts: boolean) {
  if (!compareMode) {
    return null;
  }
  if (!secondaryObject) {
    return <MissingDataBadge />;
  }
  if (hasAnyConflicts) {
    return <ConflictsBadge />;
  }
  return <SyncedBadge />;
}
export function SyncedBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="text-success border-success/30 bg-success/10"><Check className="h-3 w-3 mr-1" />Synced</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function ConflictsBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="text-warn border-warn/30 bg-warn/10">Conflicts</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function EhrDataBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="bg-ehr/10 text-ehr">EHR Data</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function MissingDataBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="outline" className="text-destructive border-destructive/30 bg-destructive/10">Missing EHR Data</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function LinkToEntity(t0) {
  const $ = _c(3);
  const {
    href
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SquareArrowOutUpRight className="size-4" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== href) {
    t2 = <Link className="text-muted-foreground hover:text-foreground" href={href} target="_blank" rel="noopener noreferrer">{t1}</Link>;
    $[1] = href;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function EntityDropdownMenu(t0) {
  const $ = _c(11);
  const {
    cardActionsProps,
    customProps: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const customProps = t2;
  let t3;
  if ($[2] !== cardActionsProps.customProps) {
    t3 = cardActionsProps.customProps ?? [];
    $[2] = cardActionsProps.customProps;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== customProps || $[5] !== t3) {
    t4 = [...t3, ...customProps];
    $[4] = customProps;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const mergedCustomProps = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DropdownMenuTrigger asChild={true}><Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"><MoreHorizontal className="h-4 w-4" /><span className="sr-only">Open menu</span></Button></DropdownMenuTrigger>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== cardActionsProps || $[9] !== mergedCustomProps) {
    t6 = <DropdownMenu>{t5}<CardActions {...cardActionsProps} isContextMenu={false} customProps={mergedCustomProps} /></DropdownMenu>;
    $[8] = cardActionsProps;
    $[9] = mergedCustomProps;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}